import React, { useState, useEffect, useCallback, useContext } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Button from "@material-ui/core/Button";
import Grid from "@material-ui/core/Grid";
import Box from "@material-ui/core/Box";
import Chip from "@material-ui/core/Chip";
import Typography from "@material-ui/core/Typography";
import TextField from "@material-ui/core/TextField";
import Checkbox from "@material-ui/core/Checkbox";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import LinearProgress from "@material-ui/core/LinearProgress";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import CircularProgress from "@material-ui/core/CircularProgress";
import GetAppIcon from "@material-ui/icons/GetApp";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import RefreshIcon from "@material-ui/icons/Refresh";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import { format, parseISO } from "date-fns";
import { toast } from "react-toastify";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import ForbiddenPage from "../../components/ForbiddenPage";
import ConfirmationModal from "../../components/ConfirmationModal";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { socketConnection } from "../../services/socket";

/**
 * Exportacao de dados da empresa (portabilidade / LGPD).
 * Cada admin exporta SOMENTE a propria empresa: o backend usa o companyId do
 * token e ignora qualquer companyId enviado pelo frontend.
 * Docs: docs/features/export-dados-empresa.md
 */

const useStyles = makeStyles(theme => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "auto",
    ...theme.scrollbarStyles
  },
  formPaper: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  aviso: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(1),
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
    borderRadius: 4,
    backgroundColor: "rgba(245, 158, 11, 0.12)"
  },
  progressCell: {
    minWidth: 160
  }
}));

const STATUS_LABEL = {
  pending: { label: "Na fila", color: "default" },
  processing: { label: "Gerando", color: "primary" },
  done: { label: "Pronto", color: "primary" },
  error: { label: "Falhou", color: "secondary" },
  expired: { label: "Expirado", color: "default" }
};

const formatBytes = bytes => {
  if (!bytes) return "-";
  const mb = bytes / 1024 / 1024;
  if (mb < 1) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${mb.toFixed(1)} MB`;
};

const formatDate = value => {
  if (!value) return "-";
  try {
    return format(parseISO(value), "dd/MM/yyyy HH:mm");
  } catch (err) {
    return "-";
  }
};

const CompanyDataExport = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const [exports, setExports] = useState([]);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const [scope, setScope] = useState({
    messages: true,
    tickets: true,
    contacts: true,
    smcFormat: false,
    media: false
  });
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const isAdmin = user?.profile === "admin" || user?.super;

  const fetchExports = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/company-exports");
      setExports(Array.isArray(data) ? data : []);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAdmin) fetchExports();
  }, [isAdmin, fetchExports]);

  // Progresso em tempo real. O namespace do socket e o da empresa, entao o
  // andamento de uma empresa nao chega em outra.
  useEffect(() => {
    if (!isAdmin || !user?.companyId) return undefined;

    const socket = socketConnection({ user });
    const evento = `company-${user.companyId}-dataExport`;

    const onEvent = data => {
      setExports(atuais =>
        atuais.map(item =>
          item.id === data.exportId
            ? {
                ...item,
                status: data.status || item.status,
                progress:
                  typeof data.progress === "number" ? data.progress : item.progress,
                rowCounts: data.rowCounts || item.rowCounts,
                fileSize: data.fileSize ?? item.fileSize
              }
            : item
        )
      );
      if (data.action === "complete") {
        toast.success("Exportação concluída. O download já está disponível.");
        fetchExports();
      }
      if (data.action === "error") {
        toast.error("A exportação falhou. Tente novamente ou fale com o suporte.");
        fetchExports();
      }
    };

    socket.on(evento, onEvent);
    return () => {
      if (socket?.off) socket.off(evento, onEvent);
    };
  }, [isAdmin, user, fetchExports]);

  const handleCreate = async () => {
    if (!scope.messages && !scope.tickets && !scope.contacts && !scope.smcFormat && !scope.media) {
      toast.warn("Escolha pelo menos um tipo de dado para exportar.");
      return;
    }
    setCreating(true);
    try {
      // NAO enviamos companyId: o backend usa o do token, de proposito.
      await api.post("/company-exports", {
        messages: scope.messages,
        tickets: scope.tickets,
        contacts: scope.contacts,
        smcFormat: scope.smcFormat,
        media: scope.media,
        dateFrom: dateFrom || null,
        dateTo: dateTo || null
      });
      toast.success(
        "Exportação iniciada. Você será avisado aqui quando o arquivo estiver pronto."
      );
      fetchExports();
    } catch (err) {
      toastError(err);
    } finally {
      setCreating(false);
    }
  };

  const handleDownload = async item => {
    setDownloadingId(item.id);
    try {
      const { data, headers } = await api.get(
        `/company-exports/${item.id}/download`,
        { responseType: "blob" }
      );
      const disposition = headers?.["content-disposition"] || "";
      const match = disposition.match(/filename="?([^"]+)"?/);
      const fileName = match ? match[1] : `export_${item.id}.zip`;

      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      fetchExports();
    } catch (err) {
      toastError(err);
    } finally {
      setDownloadingId(null);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/company-exports/${confirmDelete.id}`);
      toast.success("Exportação removida.");
      fetchExports();
    } catch (err) {
      toastError(err);
    } finally {
      setConfirmDelete(null);
    }
  };

  if (!isAdmin) return <ForbiddenPage />;

  return (
    <MainContainer>
      <ConfirmationModal
        title="Remover exportação"
        open={Boolean(confirmDelete)}
        onClose={() => setConfirmDelete(null)}
        onConfirm={handleDelete}
      >
        O arquivo será apagado do servidor. Esta ação não pode ser desfeita.
      </ConfirmationModal>

      <MainHeader>
        <Title>Exportar dados da empresa</Title>
        <MainHeaderButtonsWrapper>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<RefreshIcon />}
            onClick={fetchExports}
          >
            Atualizar
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper className={classes.mainPaper} variant="outlined">
        <Box className={classes.aviso}>
          <InfoOutlinedIcon fontSize="small" />
          <Typography variant="body2" component="div">
            A exportação traz o <strong>texto completo</strong> das conversas, os
            atendimentos e os contatos <strong>desta empresa</strong>, em CSV (abre no
            Excel). Marcando <strong>“Incluir anexos”</strong>, vêm também as fotos, áudios
            e documentos. Essa parte é bem mais pesada e o sistema a prepara{" "}
            <strong>em ritmo reduzido, sem atrapalhar o atendimento</strong> — pode levar
            de minutos a algumas horas, conforme o volume, e você é avisado aqui quando
            ficar pronta. Anexos antigos podem não estar mais no servidor: junto vai uma{" "}
            <strong>lista do que faltou</strong>. O link fica disponível por <strong>7 dias</strong>.
          </Typography>
        </Box>

        <Paper className={classes.formPaper} variant="outlined">
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="De (opcional)"
                type="date"
                fullWidth
                value={dateFrom}
                onChange={e => setDateFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Até (opcional)"
                type="date"
                fullWidth
                value={dateTo}
                onChange={e => setDateTo(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={scope.messages}
                    onChange={e =>
                      setScope({ ...scope, messages: e.target.checked })
                    }
                    color="primary"
                  />
                }
                label="Mensagens"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={scope.tickets}
                    onChange={e => setScope({ ...scope, tickets: e.target.checked })}
                    color="primary"
                  />
                }
                label="Atendimentos"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={scope.contacts}
                    onChange={e => setScope({ ...scope, contacts: e.target.checked })}
                    color="primary"
                  />
                }
                label="Contatos"
              />
              <Tooltip title="Além das planilhas, inclui uma estrutura em JSON no mesmo formato do backup usado para importação em outro sistema. Não contém senha nem credencial de conexão.">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={scope.smcFormat}
                      onChange={e =>
                        setScope({ ...scope, smcFormat: e.target.checked })
                      }
                      color="primary"
                    />
                  }
                  label="Incluir formato de backup (JSON)"
                />
              </Tooltip>
              <Tooltip title="Inclui os arquivos de fotos, áudios e documentos. Como é a parte mais pesada, o sistema monta o pacote em ritmo reduzido e cede espaço sempre que o servidor está ocupado — pode demorar, mas não atrapalha o atendimento. Anexos antigos podem não estar mais no servidor; vai uma lista do que faltou.">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={scope.media}
                      onChange={e => setScope({ ...scope, media: e.target.checked })}
                      color="primary"
                    />
                  }
                  label="Incluir anexos (fotos, áudios e documentos)"
                />
              </Tooltip>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                disabled={creating}
                onClick={handleCreate}
                startIcon={
                  creating ? <CircularProgress size={16} /> : <GetAppIcon />
                }
              >
                Exportar
              </Button>
            </Grid>
          </Grid>
          <Typography variant="caption" color="textSecondary">
            Sem data, exporta todo o histórico. É permitida uma exportação a cada 6 horas.
          </Typography>
        </Paper>

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Pedido</TableCell>
              <TableCell>Situação</TableCell>
              <TableCell className={classes.progressCell}>Andamento</TableCell>
              <TableCell align="center">Linhas</TableCell>
              <TableCell align="center">Tamanho</TableCell>
              <TableCell align="center">Solicitado</TableCell>
              <TableCell align="center">Disponível até</TableCell>
              <TableCell align="center">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!loading && exports.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Box p={2}>
                    <Typography variant="body2" color="textSecondary">
                      Nenhuma exportação ainda.
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}
            {exports.map(item => {
              const status = STATUS_LABEL[item.status] || {
                label: item.status,
                color: "default"
              };
              const totalLinhas = item.rowCounts
                ? Object.values(item.rowCounts).reduce((a, b) => a + b, 0)
                : null;
              return (
                <TableRow key={item.id}>
                  <TableCell>#{item.id}</TableCell>
                  <TableCell>
                    <Chip size="small" label={status.label} color={status.color} />
                  </TableCell>
                  <TableCell>
                    {item.status === "processing" || item.status === "pending" ? (
                      <LinearProgress
                        variant="determinate"
                        value={item.progress || 0}
                      />
                    ) : (
                      "-"
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {totalLinhas ? totalLinhas.toLocaleString("pt-BR") : "-"}
                  </TableCell>
                  <TableCell align="center">{formatBytes(item.fileSize)}</TableCell>
                  <TableCell align="center">{formatDate(item.createdAt)}</TableCell>
                  <TableCell align="center">{formatDate(item.expiresAt)}</TableCell>
                  <TableCell align="center">
                    {item.status === "done" && (
                      <Tooltip title="Baixar">
                        <IconButton
                          size="small"
                          disabled={downloadingId === item.id}
                          onClick={() => handleDownload(item)}
                        >
                          {downloadingId === item.id ? (
                            <CircularProgress size={18} />
                          ) : (
                            <GetAppIcon fontSize="small" />
                          )}
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Remover">
                      <IconButton
                        size="small"
                        onClick={() => setConfirmDelete(item)}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        {loading && (
          <Box display="flex" justifyContent="center" p={2}>
            <CircularProgress size={24} />
          </Box>
        )}
      </Paper>
    </MainContainer>
  );
};

export default CompanyDataExport;
